import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

const defaultSections = [
  { section: "scope", is_ok: null, notes: "" },
  { section: "roles", is_ok: null, notes: "" },
  { section: "compliance", is_ok: null, notes: "" },
  { section: "risk", is_ok: null, notes: "" },
  { section: "operations", is_ok: null, notes: "" },
  { section: "incidents", is_ok: null, notes: "" },
];

export default function CreateReview() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [selectedCertification, setSelectedCertification] = useState("");
  const [linkedActionables, setLinkedActionables] = useState([]);
  const [loading, setLoading] = useState(false);

  const [payload, setPayload] = useState({
    policy: "",
    policy_name: "",
    policy_version: "",
    reviewers: "",
    review_method: "",
    overall_comments: "",
    sections: defaultSections,
  });

  /* ---------------- AUTH ---------------- */
  function getAuthHeaders() {
    const token = localStorage.getItem("accessToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /* ---------------- LOAD ---------------- */
  useEffect(() => {
    fetchPolicies();
    if (id) fetchReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchPolicies() {
    try {
      const res = await axios.get("/api/policies/", {
        headers: getAuthHeaders(),
      });

      const policyData = Array.isArray(res.data) ? res.data : [];
      setPolicies(policyData);
      setFilteredPolicies(policyData);

      // 🔹 build certification list from policies
      const certMap = {};
      policyData.forEach((p) => {
        if (p.certification && p.certification_name) {
          certMap[p.certification] = p.certification_name;
        }
      });

      const certList = Object.entries(certMap).map(([id, name]) => ({
        id,
        certification_name: name,
      }));

      setCertifications(certList);
    } catch {
      setPolicies([]);
      setFilteredPolicies([]);
      setCertifications([]);
    }
  }

  async function fetchLinkedActionables(policyName) {
    if (!policyName) return;
    try {
      const res = await axios.post(
        "/api/actionables/summary/",
        { policy_or_document_name: policyName },
        { headers: getAuthHeaders() }
      );
      setLinkedActionables(res.data?.actionables || []);
    } catch {
      setLinkedActionables([]);
    }
  }

  async function fetchReview() {
    setLoading(true);
    try {
      const res = await axios.get(`/api/policy-reviews/${id}/`, {
        headers: getAuthHeaders(),
      });
      const data = res.data || {};

      setPayload({
        policy: data.policy || "",
        policy_name: data.policy_name || "",
        policy_version: data.policy_version || "",
        reviewers: data.reviewers || "",
        review_method: data.review_method || "",
        overall_comments: data.overall_comments || "",
        sections: Array.isArray(data.sections)
          ? data.sections.map((s) => ({
              section: s.section,
              is_ok: s.is_ok,
              notes: s.notes,
            }))
          : defaultSections,
      });

      if (data.policy_name) fetchLinkedActionables(data.policy_name);
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- HANDLERS ---------------- */
  const onCertificationSelect = (e) => {
    const certId = e.target.value;
    setSelectedCertification(certId);

    if (!certId) {
      setFilteredPolicies(policies);
      return;
    }

    const filtered = policies.filter(
      (p) => String(p.certification) === String(certId)
    );

    setFilteredPolicies(filtered);

    // reset policy selection
    setPayload((p) => ({
      ...p,
      policy: "",
      policy_name: "",
      policy_version: "",
    }));
    setLinkedActionables([]);
  };

  const onPolicySelect = (e) => {
    const pid = e.target.value;
    const pol = filteredPolicies.find((p) => String(p.id) === pid);

    setPayload((p) => ({
      ...p,
      policy: pid,
      policy_name: pol?.policy_name || "",
      policy_version: pol?.version || "",
    }));

    pol?.policy_name
      ? fetchLinkedActionables(pol.policy_name)
      : setLinkedActionables([]);
  };

  const handleSectionChange = (index, key, value) => {
    setPayload((prev) => {
      const updated = [...prev.sections];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, sections: updated };
    });
  };

  /* ---------------- SAVE ---------------- */
  const save = async () => {
    if (!payload.policy) {
      alert("Please select a policy first.");
      return;
    }

    setLoading(true);
    try {
      const body = {
        policy: payload.policy,
        reviewers: payload.reviewers,
        review_method: payload.review_method,
        overall_comments: payload.overall_comments,
        sections: payload.sections,
      };

      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };

      if (id) {
        await axios.put(`/api/policy-reviews/${id}/`, body, { headers });
        navigate(`/policy-reviews/${id}`);
      } else {
        const res = await axios.post("/api/policy-reviews/", body, { headers });
        navigate(`/policy-reviews/${res.data.id}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="pageWrapper">
      <div className="formCard">
        <div className="headerRow">
          <h1 className="pageTitle">
            {id ? "Edit Policy Review" : "Create Policy Review"}
          </h1>
          <button className="backBtn" onClick={() => navigate("/policy-reviews")}>
            ⬅ Back
          </button>
        </div>

        {/* POLICY DETAILS */}
        <div className="grid">
          <div>
            <label>Certification</label>
            <select
              className="modernInput"
              value={selectedCertification}
              onChange={onCertificationSelect}
            >
              <option value="">-- Select Certification --</option>
              {certifications.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.certification_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Policy *</label>
            <select
              className="modernInput"
              value={payload.policy}
              onChange={onPolicySelect}
            >
              <option value="">-- Select Policy --</option>
              {filteredPolicies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.policy_name}
                  {p.version ? ` (v${p.version})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Policy Version</label>
            <input
              className="modernInput"
              readOnly
              value={payload.policy_version}
            />
          </div>

          <div className="colSpanFull">
            <label>Reviewers</label>
            <input
              className="modernInput"
              placeholder="Eg: ISO Head, IT Manager"
              value={payload.reviewers}
              onChange={(e) =>
                setPayload((p) => ({ ...p, reviewers: e.target.value }))
              }
            />
          </div>

          <div className="colSpanFull">
            <label>Review Method</label>
            <input
              className="modernInput"
              value={payload.review_method}
              onChange={(e) =>
                setPayload((p) => ({
                  ...p,
                  review_method: e.target.value,
                }))
              }
            />
          </div>
        </div>

        {/* REVIEW SECTIONS */}
        <h3 className="sectionTitle">Review Sections</h3>
        <div className="sectionsGrid">
          {payload.sections.map((s, idx) => (
            <div className="sectionCard" key={s.section}>
              <h4>{s.section.toUpperCase()}</h4>

              <label>Is OK?</label>
              <select
                className="modernInput"
                value={s.is_ok === null ? "" : s.is_ok ? "yes" : "no"}
                onChange={(e) =>
                  handleSectionChange(
                    idx,
                    "is_ok",
                    e.target.value === "yes"
                      ? true
                      : e.target.value === "no"
                      ? false
                      : null
                  )
                }
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>

              <label>Notes</label>
              <textarea
                className="modernInput textarea"
                value={s.notes}
                onChange={(e) =>
                  handleSectionChange(idx, "notes", e.target.value)
                }
              />
            </div>
          ))}
        </div>

        {/* LINKED ACTIONABLES */}
        <h3 className="sectionTitle">Linked Actionables</h3>
        <div className="tableCard">
          <table className="modernTable">
            <thead>
              <tr>
                <th>Actionable Number</th>
                <th>Actionable Name</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {linkedActionables.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    No linked actionables
                  </td>
                </tr>
              ) : (
                linkedActionables.map((a, i) => (
                  <tr key={i}>
                    <td>{a.actionable_number}</td>
                    <td>{a.actionable_name}</td>
                    <td>{a.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* COMMENTS */}
        <div className="colSpanFull">
          <label>Overall Comments</label>
          <textarea
            className="modernInput textarea"
            value={payload.overall_comments}
            onChange={(e) =>
              setPayload((p) => ({
                ...p,
                overall_comments: e.target.value,
              }))
            }
          />
        </div>

        {/* ACTIONS */}
        <div className="btnRow">
          <button className="submitBtn" onClick={save} disabled={loading}>
            {loading
              ? "Saving..."
              : id
              ? "Update Review"
              : "Save Review"}
          </button>
          <button
            className="cancelBtn"
            onClick={() => navigate("/policy-reviews")}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* STYLES */}
      <style>{`
        .pageWrapper {
          padding: 40px;
          background: #f5f7fb;
          min-height: 100vh;
          font-family: Inter, sans-serif;
        }
        .formCard {
          background: #fff;
          padding: 30px;
          border-radius: 18px;
          max-width: 1000px;
          margin: auto;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .headerRow {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
        }
        .pageTitle {
          font-size: 26px;
          font-weight: 700;
          color: #0f1d60;
        }
        .grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        .colSpanFull {
          grid-column: 1 / -1;
        }
        .modernInput {
          width: 100%;
          padding: 12px;
          border-radius: 10px;
          border: 1px solid #d1d5db;
        }
        .textarea {
          min-height: 90px;
        }
        .sectionTitle {
          margin-top: 30px;
          font-size: 20px;
          font-weight: 600;
          color: #102a71;
        }
        .sectionsGrid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
        .sectionCard {
          background: #f9fafc;
          padding: 18px;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
        }
        .tableCard {
          background: #fff;
          padding: 15px;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
        }
        .modernTable {
          width: 100%;
          border-collapse: collapse;
        }
        .modernTable th {
          background: linear-gradient(135deg, #102a71, #2053c3);
          color: white;
          padding: 10px;
        }
        .modernTable td {
          padding: 10px;
          border-top: 1px solid #e5e7eb;
        }
        .btnRow {
          margin-top: 30px;
          display: flex;
          gap: 12px;
        }
        .submitBtn {
          background: #2563eb;
          color: white;
          padding: 12px 18px;
          border-radius: 10px;
          border: none;
        }
        .cancelBtn {
          background: #6b7280;
          color: white;
          padding: 12px 18px;
          border-radius: 10px;
          border: none;
        }
        .backBtn {
          background: #4b5563;
          color: white;
          padding: 10px 14px;
          border-radius: 10px;
          border: none;
        }
      `}</style>
    </div>
  );
}
